// ─────────────────────────────────────────────────────────────
// SupersetCanvas — single source of truth for the module.
// Every tunable (grid size, card defaults, endpoints, action
// types) lives here so a host page can override behaviour
// without touching component code.
// ─────────────────────────────────────────────────────────────
import { supersetUrl } from '../../Containers/Config';

const SUPERSET_CANVAS_CONFIG = {
  // ── Canvas geometry ──
  GRID: 20, // px per grid cell
  CARD_DEFAULTS: {
    W: 400, // GRID * 20
    H: 280, // GRID * 14
    MIN_W: 200,
    MIN_H: 160,
  },
  HISTORY_LIMIT: 30,

  // ── Superset instance ──
  BASE_URL: supersetUrl,

  ENDPOINTS: {
    CHART_LIST: '/api/v1/chart/',
    CHART_DETAIL: id => `/api/v1/chart/${id}`,
    CHART_DATA: '/api/v1/chart/data',
    LOGIN: '/api/v1/security/login',
  },

  CHART_LIST_PAGE_SIZE: 100,

  // ── Auth ──
  // 'proxy'  → token comes from the Thriwe backend (SUPERSET_PROXY_LOGIN)
  // 'manual' → user supplies URL + credentials/token in the browser
  AUTH_MODE: 'proxy',
  // Manual connect is a debugging aid only. Never default this to true in
  // production builds — it puts Superset credentials in the browser.
  ALLOW_MANUAL_CONNECT: process.env.REACT_APP_SUPERSET_CANVAS_MANUAL_CONNECT === 'true',

  // ── Redux action types ──
  ACTIONS: {
    PROXY_LOGIN: 'SUPERSET_PROXY_LOGIN',
    PROXY_LOGIN_EMPTY: 'SUPERSET_PROXY_LOGIN_EMPTY',
    CSRF_TOKEN: 'SUPERSET_CSRF_TOKEN',

    CHART_LIST: 'SUPERSET_CANVAS_CHART_LIST',
    CHART_LIST_EMPTY: 'SUPERSET_CANVAS_CHART_LIST_EMPTY',
    CHART_DATA: 'SUPERSET_CANVAS_CHART_DATA',
    CHART_DATA_EMPTY: 'SUPERSET_CANVAS_CHART_DATA_EMPTY',

    ADD_CARD: 'SUPERSET_CANVAS_ADD_CARD',
    MOVE_CARD: 'SUPERSET_CANVAS_MOVE_CARD',
    RESIZE_CARD: 'SUPERSET_CANVAS_RESIZE_CARD',
    REMOVE_CARD: 'SUPERSET_CANVAS_REMOVE_CARD',
    SELECT_CARD: 'SUPERSET_CANVAS_SELECT_CARD',
    UPDATE_CARD_OPTIONS: 'SUPERSET_CANVAS_UPDATE_CARD_OPTIONS',
    SET_CARDS: 'SUPERSET_CANVAS_SET_CARDS',
    CLEAR_CANVAS: 'SUPERSET_CANVAS_CLEAR',
    PUSH_HISTORY: 'SUPERSET_CANVAS_PUSH_HISTORY',
    UNDO: 'SUPERSET_CANVAS_UNDO',
    RESET_INSTANCE: 'SUPERSET_CANVAS_RESET_INSTANCE',
  },

  // ── Export payload version ──
  EXPORT_VERSION: '3.0',

  // ── Chart panel categories ──
  CATEGORIES: [
    { key: 'all', label: 'All' },
    { key: 'popular', label: 'Popular' },
    { key: 'time', label: 'Time' },
    { key: 'part', label: 'Part-of' },
    { key: 'comparison', label: 'Compare' },
    { key: 'distribution', label: 'Dist.' },
    { key: 'flow', label: 'Flow' },
    { key: 'geo', label: 'Geo' },
    { key: 'other', label: 'Other' },
  ],

  // ── Palette ──
  COLORS: {
    SERIES: ['#2563eb', '#1D9E75', '#D85A30', '#BA7517', '#D4537E', '#639922'],
    PRIMARY: '#2563eb',
    INK: '#18170f',
    MUTED: '#6a6960',
    FAINT: '#a5a49c',
    LINE: 'rgba(0,0,0,0.09)',
    SURFACE: '#fff',
    CANVAS: '#f4f3f0',
    DANGER: '#D85A30',
    SUCCESS: '#0F6E56',
  },

  CATEGORY_COLORS: {
    popular: { bg: '#E6F1FB', ic: '#185FA5' },
    time: { bg: '#EAF3DE', ic: '#3B6D11' },
    part: { bg: '#FAEEDA', ic: '#854F0B' },
    comparison: { bg: '#E6F1FB', ic: '#185FA5' },
    distribution: { bg: '#FAECE7', ic: '#993C1D' },
    flow: { bg: '#E1F5EE', ic: '#0F6E56' },
    geo: { bg: '#E1F5EE', ic: '#0F6E56' },
    other: { bg: '#EEEDFE', ic: '#3C3489' },
  },
};

export default SUPERSET_CANVAS_CONFIG;
